import type { DialogRootProps, DrawerRootProps } from '@chakra-ui/react';
import { Dialog, Drawer } from '@chakra-ui/react';
import { createContext, type FC, useContext } from 'react';

import { DragDrawerContent } from './DragDrawer/DragDrawerContent';
import { useIsDesktop } from './MobileOrDesktop';

const DialogOrDrawerContext = createContext<boolean | undefined>(undefined);

const Root: FC<DrawerRootProps & DialogRootProps & JSX.IntrinsicAttributes> = (
  props,
) => {
  const isDesktop = useIsDesktop();

  return (
    <DialogOrDrawerContext.Provider value={isDesktop}>
      <RootInner {...props} />
    </DialogOrDrawerContext.Provider>
  );
};
const RootInner = hocDialogOrDrawer(
  'DialogOrDrawerRoot',
  Drawer.Root,
  Dialog.Root,
);
const Content = hocDialogOrDrawer(
  'DialogOrDrawerContent',
  Drawer.Content,
  Dialog.Content,
);
const Positioner = hocDialogOrDrawer(
  'DialogOrDrawerPositioner',
  Drawer.Positioner,
  Dialog.Positioner,
);
const DragContent = hocDialogOrDrawer(
  'DialogOrDragDrawerContent',
  DragDrawerContent,
  Dialog.Content,
);
const Backdrop = hocDialogOrDrawer(
  'DialogOrDrawerBackdrop',
  Drawer.Backdrop,
  Dialog.Backdrop,
);
const Body = hocDialogOrDrawer('DialogOrDrawerBody', Drawer.Body, Dialog.Body);
const Header = hocDialogOrDrawer(
  'DialogOrDrawerHeader',
  Drawer.Header,
  Dialog.Header,
);
const Footer = hocDialogOrDrawer(
  'DialogOrDrawerFooter',
  Drawer.Footer,
  Dialog.Footer,
);

const CloseTrigger = hocDialogOrDrawer(
  'DialogOrDrawerCloseTrigger',
  Drawer.CloseTrigger,
  Dialog.CloseTrigger,
);

const Title = hocDialogOrDrawer(
  'DialogOrDrawerTitle',
  Drawer.Title,
  Dialog.Title,
);

const Description = hocDialogOrDrawer(
  'DialogOrDrawerDescription',
  Drawer.Description,
  Dialog.Description,
);

export const DialogOrDrawer = {
  Root,
  Positioner,
  Content,
  DragContent,
  Backdrop,
  Body,
  Header,
  Footer,
  CloseTrigger,
  Title,
  Description,
};

function hocDialogOrDrawer<D, M>(
  name: string,
  DrawerPart: FC<D>,
  DialogPart: FC<M>,
) {
  const Hoc = (props: D & M & JSX.IntrinsicAttributes) => {
    const isDesktop = useContext(DialogOrDrawerContext);
    return (
      <>
        {isDesktop === false && <DrawerPart {...props} />}
        {isDesktop === true && <DialogPart {...props} />}
      </>
    );
  };
  Hoc.displayName = name;
  return Hoc;
}
